import React, { ReactElement, RefObject, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  Col,
  Filter,
  FilterListIcon,
  FormItem,
  Layout,
  Row,
  Select,
} from 'elmo-elements';

import { getQueryString, pushParamsToUrl, sanitizeObject } from '@/lib/util';
import MyLibraryList from '@/pages/MyLibrary/components/List';
import NewModule from '@/pages/MyLibrary/components/NewModule';
import MyElearningHeader from '@/pages/MyLibrary/components/Header';
import {
  useGetElearningAllowedCategoriesQuery,
  useGetMyLibraryQuery,
} from '@/modules/services';
import { appData } from '@/lib/data';
import { useUserHasCapabilities } from '@/lib/auth/useUserHasCapabilities';
import { CourseLibraryPermissions } from '@/lib/auth/permissions/courseLibraryPermissions.enum';
import CourseLibraryRoutes from '@/pages/CourseLibrary/CourseLibraryRoutes';
import NoResult from '@/components/NoModulesResult';

const MyLibraryView = (): ReactElement => {
  const canAccessCourseLibrary = useUserHasCapabilities([
    CourseLibraryPermissions.VIEW_COURSE_LIBRARY,
  ]);
  const location = useLocation();
  const history = useHistory();
  const filtersRef: RefObject<any> = React.createRef();
  const statusFilters = [
    { value: 'all', label: 'All' },
    { value: 'draft', label: 'Draft' },
    { value: 'review', label: 'Review' },
    { value: 'live', label: 'Live' },
  ];
  const getAllowedCategoriesDataStatus =
    useGetElearningAllowedCategoriesQuery();
  const { data } = useGetMyLibraryQuery(
    getQueryString(location.search, {
      ...appData.pagination.defaultParams,
      sort: 'ma',
      order: 'DESC',
    }),
    { skip: !getAllowedCategoriesDataStatus.data },
  );
  const searchParams = new URLSearchParams(location.search);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<
    { value: string; label: string } | undefined | null
  >(null);
  const activeCategoryFilter: any = searchParams.has('category')
    ? getAllowedCategoriesDataStatus.data?.categories
        .map((category) => ({ value: category.id, label: category.name }))
        .find((cat) => cat.value === searchParams.get('category'))
    : null;

  useEffect(() => {
    if (!getAllowedCategoriesDataStatus.data || !searchParams.has('category'))
      return;

    const paramCategory =
      getAllowedCategoriesDataStatus.data?.categories?.filter(
        (category) => category.id === searchParams.get('category'),
      );

    if (!paramCategory.length) {
      // remove invalid category id from url
      searchParams.delete('category');
      history.replace({ search: searchParams.toString() });
    }
  }, [location, getAllowedCategoriesDataStatus]);

  const applyFilters = (newFilters: object) => {
    const filters = sanitizeObject({
      ...getQueryString(),
      ...newFilters,
    });
    history.replace({});
    pushParamsToUrl(filters, history);
  };

  if (!data) {
    return <div />;
  }
  return (
    <>
      <Layout.Header>
        <MyElearningHeader />
      </Layout.Header>
      <Layout.Content>
        <div className="my-library-filters">
          {statusFilters.map((status, statusIndex) => (
            <Button
              className={
                (status.value === 'all' && !searchParams.has('status')) ||
                status.value === searchParams.get('status')
                  ? 'active status-filter'
                  : 'status-filter'
              }
              key={`status-${statusIndex}`}
              onClick={() => {
                applyFilters({ status: status.value, page: 1 });
              }}
            >
              {status.label}
            </Button>
          ))}
        </div>
        {data && data.data.length === 0 ? (
          <NoResult>
            {canAccessCourseLibrary ? (
              <p>
                Add modules from{' '}
                <a href={CourseLibraryRoutes.path}>Course Library</a> or create
                a new one.
              </p>
            ) : (
              <p>Please create new modules.</p>
            )}
          </NoResult>
        ) : (
          <MyLibraryList />
        )}
        <NewModule />
      </Layout.Content>
    </>
  );
};

export default MyLibraryView;
